import type { CandidateStatus } from '@eduvy/core';
import type { CandidateRow } from './api';
import { formatDuration } from './format';

/** Pure helpers for the job pipeline board: which column a lead sits in, and its one-line progress hint. */

export type ColumnKey = 'new' | 'reaching' | 'qualified' | 'review' | 'out' | 'assessment' | 'meeting' | 'decided';

export interface ColumnDef {
  key: ColumnKey;
  label: string;
  statuses: CandidateStatus[];
  /** Accent colour for the column dot. */
  accent: string;
}

export const COLUMNS: ColumnDef[] = [
  { key: 'new', label: 'New', statuses: ['NEW'], accent: '#605e6c' },
  { key: 'reaching', label: 'Reaching out', statuses: ['QUEUED', 'INVITED', 'CALLING', 'IN_INTERVIEW', 'INTERVIEWED'], accent: '#a8279a' },
  { key: 'qualified', label: 'Qualified', statuses: ['QUALIFIED'], accent: '#10b981' },
  { key: 'review', label: 'Needs review', statuses: ['NEEDS_REVIEW'], accent: '#f59e0b' },
  { key: 'out', label: 'Not qualified · unreachable', statuses: ['NOT_QUALIFIED', 'UNREACHABLE'], accent: '#f43f5e' },
  { key: 'assessment', label: 'Skills assessment', statuses: ['ASSESSMENT_INVITED', 'IN_ASSESSMENT', 'ASSESSED'], accent: '#6366f1' },
  { key: 'meeting', label: 'Recruiter interview', statuses: ['MEETING_INVITED'], accent: '#0ea5e9' },
  { key: 'decided', label: 'Decided', statuses: ['SELECTED', 'SHORTLISTED', 'REJECTED', 'ON_HOLD', 'WITHDRAWN'], accent: '#7e2ea6' },
];

const COLUMN_OF = new Map<CandidateStatus, ColumnKey>(COLUMNS.flatMap((c) => c.statuses.map((s) => [s, c.key] as const)));

export function columnFor(status: CandidateStatus): ColumnKey {
  return COLUMN_OF.get(status) ?? 'new';
}

/** Group rows into board columns. Scored columns sort best-first; others keep the incoming order. */
export function groupByColumn(rows: CandidateRow[]): Record<ColumnKey, CandidateRow[]> {
  const out: Record<ColumnKey, CandidateRow[]> = { new: [], reaching: [], qualified: [], review: [], out: [], assessment: [], meeting: [], decided: [] };
  for (const r of rows) out[columnFor(r.status)].push(r);
  const byScore = (a: CandidateRow, b: CandidateRow) => (b.score?.overallScore ?? -1) - (a.score?.overallScore ?? -1);
  out.qualified.sort(byScore);
  out.review.sort(byScore);
  // Assessed first, best skills score first; then those still to take it.
  const bySkills = (a: CandidateRow, b: CandidateRow) => (b.skills?.overallScore ?? -1) - (a.skills?.overallScore ?? -1);
  out.assessment.sort(bySkills);
  return out;
}

export type ProgressHint = { kind: 'live' | 'scheduled' | 'retry' | 'info'; text: string };

/**
 * The one-line status under a card in "Reaching out" (and a few other places).
 * `now` is injected so the helper stays pure and testable.
 */
export function progressHint(
  c: CandidateRow,
  job: { maxCallAttempts: number; retryAfterMinutes: number },
  now: number,
  fmtTime: (d: Date) => string = (d) => d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }),
): ProgressHint | null {
  const l = c.latest;
  if (c.status === 'IN_INTERVIEW' || c.status === 'IN_ASSESSMENT' || l?.status === 'IN_PROGRESS') {
    const started = l?.startedAt ? new Date(l.startedAt).getTime() : null;
    const clock = started ? ` · ${formatDuration(now - started)}` : '';
    const what = l?.stage === 'ASSESSMENT' ? 'In assessment' : l?.stage === 'RECRUITER' ? 'In recruiter interview' : l?.channel === 'PHONE' ? 'On a call' : 'In interview';
    return { kind: 'live', text: `${what}${clock}` };
  }
  if (l?.scheduledAt && new Date(l.scheduledAt).getTime() > now - 60 * 60_000 && !['COMPLETED', 'CANCELLED'].includes(l.status)) {
    const what = l.stage === 'ASSESSMENT' ? 'Assessment' : l.stage === 'RECRUITER' ? 'Recruiter interview' : 'Video interview';
    return { kind: 'scheduled', text: `${what} booked · ${formatSlot(new Date(l.scheduledAt), now, fmtTime)}` };
  }
  if (l?.channel === 'PHONE') {
    if (l.status === 'RINGING') return { kind: 'info', text: `Call ${l.attemptNo} of ${job.maxCallAttempts} · ringing` };
    if ((l.status === 'NO_ANSWER' || l.status === 'BUSY') && l.attemptNo < job.maxCallAttempts && c.status !== 'UNREACHABLE') {
      const base = l.endedAt ? new Date(l.endedAt).getTime() : null;
      const next = base ? ` ${fmtTime(new Date(base + job.retryAfterMinutes * 60_000))}` : '';
      return { kind: 'retry', text: `Call ${l.attemptNo + 1} of ${job.maxCallAttempts} · next try${next}` };
    }
    if (l.status === 'PENDING') return { kind: 'info', text: `Call ${l.attemptNo} of ${job.maxCallAttempts} · queued` };
  }
  switch (c.status) {
    case 'QUEUED':
      return { kind: 'info', text: 'Queued' };
    case 'INVITED':
      return { kind: 'info', text: 'Link sent · waiting' };
    case 'CALLING':
      return { kind: 'info', text: 'Calling' };
    case 'INTERVIEWED':
      return { kind: 'info', text: 'Interview done · scoring' };
    case 'ASSESSMENT_INVITED':
      return { kind: 'info', text: 'Assessment link sent · waiting' };
    case 'ASSESSED':
      return { kind: 'info', text: c.skills ? `Skills score ${c.skills.overallScore} · review` : 'Assessment done · scoring' };
    case 'MEETING_INVITED':
      return { kind: 'info', text: 'Invited · booking a time' };
    default:
      return null;
  }
}

function formatSlot(d: Date, now: number, fmtTime: (d: Date) => string): string {
  const today = new Date(now);
  const sameDay = d.toDateString() === today.toDateString();
  const tomorrow = new Date(now + 86_400_000).toDateString() === d.toDateString();
  if (sameDay) return `today ${fmtTime(d)}`;
  if (tomorrow) return `tomorrow ${fmtTime(d)}`;
  return `${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} ${fmtTime(d)}`;
}

/** Case-insensitive match on name, email, phone digits and sector. */
export function matchesSearch(c: CandidateRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    c.name.toLowerCase().includes(q) ||
    (c.email?.toLowerCase().includes(q) ?? false) ||
    (c.phoneE164?.includes(q.replace(/\s/g, '')) ?? false) ||
    (c.sector?.toLowerCase().includes(q) ?? false)
  );
}

/** Why a candidate is where they are: the recruiter's reason, else the latest verdict's reasons. */
export function whyLines(c: CandidateRow): string[] {
  const lines: string[] = [];
  if (c.decision?.reason) lines.push(c.decision.reason);
  const stage = c.skills && ['ASSESSED', 'MEETING_INVITED', 'SELECTED'].includes(c.status) ? c.skills : c.score;
  // Rules that fired come first; the plain score line after them.
  const reasons = [...(stage?.reasons ?? [])].sort((a, b) => Number(/^Score /.test(a)) - Number(/^Score /.test(b)));
  lines.push(...reasons);
  if (!lines.length && c.status === 'UNREACHABLE') lines.push('Every attempt went unanswered');
  // How the last attempt ended, unless a link or call is open again (an old ending would mislead).
  const open = c.latest && ['PENDING', 'INVITED', 'RINGING', 'IN_PROGRESS'].includes(c.latest.status);
  if (!lines.length && !open && c.latest?.endReason && !['completed', 'none'].includes(c.latest.endReason)) lines.push(c.latest.endReason.replace(/_/g, ' '));
  return lines;
}
